import type { Socket } from "socket.io-client";

type ChatSocketHandlers = {
  // Optional: the connection-lifecycle listeners (connect/disconnect/connect_error)
  // are owned by ChatLayout's stable socket-lifecycle effect so they fire once per
  // real connection, NOT on every channel/space switch. The per-channel handler
  // registration passes these as undefined.
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (err: Error) => void;
  onAuthAccepted: (payload: { userId: string; name: string; avatarUrl?: string | null }) => void;
  // Optional since step 4.3: useSpaces registers its own void:list/void:ready
  // listeners.
  onVoidList?: (payload: { voids: any[] }) => void;
  onVoidReady?: (payload: any) => void;
  onChannelList: (payload: { voidId: string; channels: any[] }) => void;
  onChannelBacklog: (payload: any) => void;
  onKanbanState: (payload: any) => void;
  onFormState: (payload: any) => void;
  onNewMessage: (payload: any) => void;
  onMessageAck?: (payload: any) => void;
  onSeen: (payload: any) => void;
  onEdited: (payload: any) => void;
  onDeleted: (payload: any) => void;
  onReactions: (payload: any) => void;
  // Optional since step 3.1: usePresence registers its own typing/presence
  // listeners; kept in the interface for any future caller that wants them.
  onTypingStart?: (payload: any) => void;
  onTypingStop?: (payload: any) => void;
  onPresenceRoom?: (payload: any) => void;
  onPresenceSpace?: (payload: any) => void;
  onPresenceGlobal?: (payload: any) => void;
  onVoicePresence?: (payload: any) => void;
  onChannelsReordered?: (payload: { spaceId: string }) => void;
  onJoinRequestsChanged?: (payload: { spaceId?: string }) => void;
  onJoinRequestsNew?: (payload: any) => void;
  onJoinRequestsResolved?: (payload: { spaceId?: string; requestId?: string }) => void;
  // Optional since step 3.3: useNotifications registers its own
  // join-requests:new/resolved, user:update, and friends:update listeners.
  onUserUpdate?: (payload: any) => void;
  onFormAnswer: (payload: any) => void;
  onUserStatus: (payload: any) => void;
  onSpaceNotify: (payload: any) => void;
  onFriendsUpdate?: (payload: any) => void;
  // Cross-device read sync: this user read a channel on another device/tab, so
  // dismiss its notification + unread badge here.
  onNotificationClear?: (payload: { channelId: string; spaceId?: string }) => void;
  // The user marked everything read (on any device) — clear all local unread here.
  onUnreadReset?: () => void;
};

/**
 * Centralizes registration/deregistration of chat-related socket events.
 * Call this hook once in ChatLayout to wire listeners.
 */
export function registerChatSocketEvents(socket: Socket, handlers: ChatSocketHandlers) {
  if (!socket) return () => {};
  const {
    onConnect,
    onDisconnect,
    onError,
    onAuthAccepted,
    onVoidList,
    onVoidReady,
    onChannelList,
    onChannelBacklog,
    onKanbanState,
    onFormState,
    onNewMessage,
    onMessageAck,
    onSeen,
    onEdited,
    onDeleted,
    onReactions,
    onTypingStart,
    onTypingStop,
    onPresenceRoom,
    onPresenceSpace,
    onPresenceGlobal,
    onVoicePresence,
    onChannelsReordered,
    onJoinRequestsChanged,
    onJoinRequestsNew,
    onJoinRequestsResolved,
    onUserUpdate,
    onFormAnswer,
    onUserStatus,
    onSpaceNotify,
    onFriendsUpdate,
    onNotificationClear,
    onUnreadReset,
  } = handlers;

  if (onConnect) socket.on("connect", onConnect);
  if (onDisconnect) socket.on("disconnect", onDisconnect);
  if (onError) socket.on("connect_error", onError);
  socket.on("auth:accepted", onAuthAccepted);
  if (onVoidList) socket.on("void:list", onVoidList);
  if (onVoidReady) socket.on("void:ready", onVoidReady);
  socket.on("channel:list", onChannelList);
  socket.on("channel:backlog", onChannelBacklog);
  socket.on("kanban:state", onKanbanState);
  socket.on("form:state", onFormState);
  socket.on("message:new", onNewMessage);
  if (onMessageAck) socket.on("message:ack", onMessageAck);
  socket.on("message:seen", onSeen);
  socket.on("message:edited", onEdited);
  socket.on("message:deleted", onDeleted);
  socket.on("message:reactions", onReactions);
  if (onTypingStart) socket.on("typing:start", onTypingStart);
  if (onTypingStop) socket.on("typing:stop", onTypingStop);
  if (onPresenceRoom) socket.on("presence:room", onPresenceRoom);
  if (onPresenceSpace) socket.on("presence:space", onPresenceSpace);
  if (onPresenceGlobal) socket.on("presence:global", onPresenceGlobal);
  if (onVoicePresence) socket.on("voice:presence", onVoicePresence);
  if (onChannelsReordered) socket.on("channels:reordered", onChannelsReordered);
  if (onJoinRequestsChanged) socket.on("join-requests:changed", onJoinRequestsChanged);
  if (onJoinRequestsNew) socket.on("join-requests:new", onJoinRequestsNew);
  if (onJoinRequestsResolved) socket.on("join-requests:resolved", onJoinRequestsResolved);
  if (onUserUpdate) socket.on("user:update", onUserUpdate);
  socket.on("form:answer", onFormAnswer);
  socket.on("user:status", onUserStatus);
  socket.on("user:notify", onSpaceNotify);
  if (onFriendsUpdate) socket.on("friends:update", onFriendsUpdate);
  if (onNotificationClear) socket.on("notification:clear", onNotificationClear);
  if (onUnreadReset) socket.on("unread:reset", onUnreadReset);
  const onRateLimited = ({ event, retryAfterSec }: { event: string; retryAfterSec: number }) => {
    const label = event === 'message:send' ? 'sending messages' : event;
    const seconds = Math.ceil(retryAfterSec || 5);
    // Use the global toast utility — imported lazily to avoid circular deps
    import('../../lib/ui')
      .then(({ toast }) => toast(`You're doing that too fast. Please wait ${seconds}s before ${label}.`, 'error'))
      .catch(() => console.warn('[rate-limit]', event, retryAfterSec));
  };
  socket.on("rate:limited", onRateLimited);

  return () => {
    if (onConnect) socket.off("connect", onConnect);
    if (onDisconnect) socket.off("disconnect", onDisconnect);
    if (onError) socket.off("connect_error", onError);
    socket.off("auth:accepted", onAuthAccepted);
    if (onVoidList) socket.off("void:list", onVoidList);
    if (onVoidReady) socket.off("void:ready", onVoidReady);
    socket.off("channel:list", onChannelList);
    socket.off("channel:backlog", onChannelBacklog);
  socket.off("kanban:state", onKanbanState);
  socket.off("form:state", onFormState);
  socket.off("message:new", onNewMessage);
  if (onMessageAck) socket.off("message:ack", onMessageAck);
  socket.off("message:seen", onSeen);
    socket.off("message:edited", onEdited);
    socket.off("message:deleted", onDeleted);
    socket.off("message:reactions", onReactions);
    if (onTypingStart) socket.off("typing:start", onTypingStart);
    if (onTypingStop) socket.off("typing:stop", onTypingStop);
    if (onPresenceRoom) socket.off("presence:room", onPresenceRoom);
    if (onPresenceSpace) socket.off("presence:space", onPresenceSpace);
    if (onPresenceGlobal) socket.off("presence:global", onPresenceGlobal);
    if (onVoicePresence) socket.off("voice:presence", onVoicePresence);
    if (onChannelsReordered) socket.off("channels:reordered", onChannelsReordered);
    if (onJoinRequestsChanged) socket.off("join-requests:changed", onJoinRequestsChanged);
    if (onJoinRequestsNew) socket.off("join-requests:new", onJoinRequestsNew);
    if (onJoinRequestsResolved) socket.off("join-requests:resolved", onJoinRequestsResolved);
    if (onUserUpdate) socket.off("user:update", onUserUpdate);
    socket.off("form:answer", onFormAnswer);
    socket.off("user:status", onUserStatus);
    socket.off("user:notify", onSpaceNotify);
    if (onFriendsUpdate) socket.off("friends:update", onFriendsUpdate);
    if (onNotificationClear) socket.off("notification:clear", onNotificationClear);
    if (onUnreadReset) socket.off("unread:reset", onUnreadReset);
    socket.off("rate:limited", onRateLimited);
  };
}
